import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Harsil = () => {
  const tourData = {
    name: "Harsil Valley",
    duration: "03 Nights 04 Days",
    price: "₹ 5,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755110919/qkkxb4nwbgdsrgzjubi3.jpg",
    altitude: "7,860 ft",
    difficulty: "Easy",
    region: "Uttarakhand",
    highlights: ["Gartang Gali", "Gangotri Temple", "Mountain views"],
    bestTime: "May to October",
    description: "The Harsil Valley tour is a perfect weekend getaway in Uttarakhand that combines adventure with spiritual experiences. This easy tour offers skywalk trekking, temple visits, and scenic mountain views, making it ideal for families and adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Harsil Valley Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Harsil Valley tour in Uttarakhand. Weekend getaway with Gartang Gali skywalk and Gangotri Temple visit." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Weekend getaway with Gartang Gali skywalk and Gangotri Temple visit
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This tour takes you to the beautiful Harsil Valley in Uttarakhand, 
                    offering a perfect blend of adventure and spiritual experiences. The journey 
                    includes the world's deadliest skywalk at Gartang Gali, temple visits, 
                    and scenic mountain views, making it an ideal weekend escape.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Gartang Gali skywalk adventure</li>
                    <li>Gangotri Temple visit</li>
                    <li>Mountain views</li>
                    <li>Bagori Village exploration</li>
                    <li>Lamatop trek</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Spiritual experiences</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Departure to Harsil Valley</h4>
                    <ul>
                      <li>Departure in the evening to Harsil Valley</li>
                      <li>The group will assemble at the pickup point</li>
                      <li>You'll be introduced to the team captains and the whole group</li>
                      <li>Halt for dinner in between (not on us)</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Uttarkashi & Reach Harsil Valley</h4>
                    <ul>
                      <li>Reach Uttarkashi by morning, freshen up & have breakfast</li>
                      <li>Resume your journey to Harsil Valley</li>
                      <li>Reach Harsil in the evening and check-in at stay</li>
                      <li>Freshen up & rest for some time</li>
                      <li>Enjoy the views in the lap of nature with evening tea and snacks</li>
                      <li>Followed by dinner and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Gartang Gali - Gangotri Temple</h4>
                    <ul>
                      <li>Wake up early morning, have breakfast</li>
                      <li>Drive to Lanka Bridge from where Gartang Gali trek starts (2.5 Kms trek)</li>
                      <li>Gartang Gali is known as the world's deadliest skywalk</li>
                      <li>Later kick start your journey for Gangotri Temple</li>
                      <li>Visit Gangotri Temple, a small town with the temple of Goddess Ganga</li>
                      <li>Head back to our stay in Harsil</li>
                      <li>Dinner will be served and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Bagori Village and Lamatop</h4>
                    <ul>
                      <li>Wake up in the early morning</li>
                      <li>Have your breakfast & trek to Lama top (2.5 Kms trek)</li>
                      <li>Visit Bagori Village</li>
                      <li>Board our vehicle to Delhi late afternoon</li>
                      <li>Pitstop in between for dinner</li>
                      <li>Next day early morning we will reach Delhi</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Accommodation: A 2-night stay in a Home Stay or Hotel in Harsil</li>
                    <li>Meals: 4 meals, specifically 2 breakfasts and 2 dinners</li>
                    <li>Transfer: Transportation to/from in a tempo traveler</li>
                    <li>Sightseeing: All sightseeing activities as mentioned in the itinerary</li>
                    <li>Personnel: A tour coordinator</li>
                    <li>Permits: All necessary permits</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Entrance Fees: Entrance fees for any monument or activity during sightseeing</li>
                    <li>Meals: Any lunch and other meals not explicitly mentioned in the package</li>
                    <li>Delay Costs: Additional accommodation or food costs incurred due to any travel delays</li>
                    <li>Personal Expenses: Any personal expenses</li>
                    <li>Taxes: 5% GST</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>7,860 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>500 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>QUAD SHARING:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 08:00 PM to 09:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Gartang Gali skywalk adventure</li>
                    <li>Gangotri Temple visit</li>
                    <li>Mountain views</li>
                    <li>Bagori Village exploration</li>
                    <li>Lamatop trek</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Spiritual experiences</li>
                    <li>Tour coordinator support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Harsil;
